import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { Fragment, type FC, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from "@mui/material";
import { DATA_FLOW_QUERY } from "../../../graphql/queries/dataFlow";
import type { DataFlowStep, RawGetDataFlow } from "../types";
import { filterToNode, transformDataFlow } from "../transformDataFlow";
import { DataFlowViewer } from "../components/DataFlowViewer";
import { NodeInfoPanel } from "../components/NodeInfoPanel";

const NO_PROJECT_LABEL = "No Project Given";

function groupByProject(steps: DataFlowStep[]): [string, DataFlowStep[]][] {
  const map = new Map<string, DataFlowStep[]>();
  for (const step of steps) {
    const key = step.project || NO_PROJECT_LABEL;
    const group = map.get(key);
    if (group) {
      group.push(step);
    } else {
      map.set(key, [step]);
    }
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

type SelectedItem = { type: "upload" | "download"; name: string };

const FlowsPage: FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(() => {
    const paramName = searchParams.get("name");
    const paramType = searchParams.get("type");
    if (paramName && (paramType === "upload" || paramType === "download")) {
      return { type: paramType, name: paramName };
    }
    return null;
  });
  const [selectedNode, setSelectedNode] = useState<DataFlowStep | null>(null);
  const [reframedNode, setReframedNode] = useState<string | null>(null);
  const [highlightedName, setHighlightedName] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToNameRef = useRef<string | null>(null);

  const { loading, error, data } = useQuery<{ getDataFlow: RawGetDataFlow }>(DATA_FLOW_QUERY);

  const dataFlow = useMemo(() => {
    if (!data?.getDataFlow) return null;
    return transformDataFlow(data.getDataFlow);
  }, [data]);

  const uploads = useMemo(() => {
    if (!dataFlow) return [];
    const stepMap = new Map(dataFlow.steps.map((s) => [s.name, s]));
    return dataFlow.steps
      .filter((s) => {
        if (s.kind !== "Sink") return false;
        return s.outputs.every((outputName) => {
          const out = stepMap.get(outputName);
          return out?.kind === "Table" && out.outputs.length === 0;
        });
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dataFlow]);

  const downloads = useMemo(() => {
    if (!dataFlow) return [];
    return dataFlow.steps.filter((s) => s.kind === "Source").sort((a, b) => a.name.localeCompare(b.name));
  }, [dataFlow]);

  // Resolves a name that may be a declarationName (from URL params) to the actual step name.
  // Falls back to the input name if no match is found.
  const resolvedStepName = useMemo(() => {
    if (!selectedItem || !dataFlow) return selectedItem?.name ?? null;
    const stepMap = new Map(dataFlow.steps.map((s) => [s.name, s]));
    if (stepMap.has(selectedItem.name)) return selectedItem.name;
    const expectedKind = selectedItem.type === "download" ? "Source" : "Sink";
    const match = dataFlow.steps.find((s) => s.declarationName === selectedItem.name && s.kind === expectedKind);
    return match?.name ?? selectedItem.name;
  }, [selectedItem, dataFlow]);

  const filteredDataFlow = useMemo(() => {
    if (!dataFlow || !selectedItem || !resolvedStepName) return null;
    return filterToNode(reframedNode ?? resolvedStepName, dataFlow);
  }, [dataFlow, selectedItem, reframedNode, resolvedStepName]);

  const handleSelect = (type: "upload" | "download", name: string) => {
    setSelectedItem({ type, name });
    setReframedNode(null);
    setSelectedNode(null);
    setHighlightedName(null);
  };

  const handleReframe = () => {
    if (!selectedNode) return;
    setReframedNode(selectedNode.name);
    setSelectedNode(null);
  };

  const handleBack = () => {
    if (reframedNode !== null) {
      setReframedNode(null);
    } else if (selectedItem !== null) {
      scrollToNameRef.current = selectedItem.name;
      setHighlightedName(selectedItem.name);
      setSelectedItem(null);
    }
    setSelectedNode(null);
  };

  useEffect(() => {
    if (selectedItem !== null || !scrollToNameRef.current || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-scroll-name="${scrollToNameRef.current}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    scrollToNameRef.current = null;
  }, [selectedItem]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (!selectedItem) {
    return (
      <Box ref={listRef} sx={{ display: "flex", gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="heading.three" sx={{ mb: 2 }}>
            Uploads
          </Typography>
          <List disablePadding>
            {groupByProject(uploads).map(([project, items]) => (
              <Fragment key={project}>
                <ListSubheader disableSticky sx={{ fontWeight: "bold", color: "primary70.main" }}>
                  {project}
                </ListSubheader>
                {items.map((upload) => (
                  <ListItem
                    key={upload.name}
                    disablePadding
                    data-scroll-name={upload.name}
                    sx={
                      upload.name === highlightedName
                        ? { backgroundColor: "primary10.main", borderRadius: 1 }
                        : undefined
                    }
                  >
                    <ListItemButton onClick={() => handleSelect("upload", upload.name)}>
                      <ListItemText primary={upload.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Fragment>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="heading.three" sx={{ mb: 2 }}>
            Downloads
          </Typography>
          <List disablePadding>
            {groupByProject(downloads).map(([project, items]) => (
              <Fragment key={project}>
                <ListSubheader disableSticky sx={{ fontWeight: "bold", color: "primary70.main" }}>
                  {project}
                </ListSubheader>
                {items.map((download) => (
                  <ListItem
                    key={download.name}
                    disablePadding
                    data-scroll-name={download.name}
                    sx={
                      download.name === highlightedName
                        ? { backgroundColor: "primary10.main", borderRadius: 1 }
                        : undefined
                    }
                  >
                    <ListItemButton onClick={() => handleSelect("download", download.name)}>
                      <ListItemText primary={download.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Fragment>
            ))}
          </List>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", height: "75vh", overflow: "hidden" }}>
        <Box
          sx={(theme) => ({
            flexGrow: 1,
            minWidth: 0,
            bgcolor: "surfaceLight10.main",
            borderRadius: "8px",
            boxShadow: theme.mixins.shadowSurfaceLight10,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            ...theme.applyStyles("dark", {
              boxShadow: theme.mixins.shadowSurfaceDark10,
            }),
          })}
        >
          <Stack
            spacing={1.5}
            sx={{
              p: 2,
              bgcolor: "surfaceLight30.main",
            }}
          >
            {reframedNode !== null && (
              <Typography
                component="button"
                variant="body.overline.regular"
                onClick={handleBack}
                title={selectedItem.name}
                aria-label={`Back to ${selectedItem.name}`}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  alignSelf: "flex-start",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  maxWidth: "100%",
                  color: "text40.main",
                  "&:hover": { color: "text50.main" },
                }}
              >
                <ArrowLeftIcon size={14} />
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Back to: {selectedItem.name}
                </Box>
              </Typography>
            )}
            <Typography
              variant="heading.seven"
              noWrap
              title={reframedNode ?? selectedItem.name}
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {reframedNode ?? selectedItem.name}
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1, position: "relative", minWidth: 0 }}>
            {filteredDataFlow && (
              <DataFlowViewer
                dataFlow={filteredDataFlow}
                onNodeSelect={setSelectedNode}
                highlightedNodeName={reframedNode ?? resolvedStepName ?? selectedItem.name}
                selectedNodeName={selectedNode?.name}
              />
            )}
          </Box>
        </Box>
        <NodeInfoPanel
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          onReframe={handleReframe}
          scriptbookId={dataFlow?.scriptbookId}
        />
      </Box>
    </Box>
  );
};

export default FlowsPage;
